/* eslint-disable @reatom/unit-naming-rule */
import { atom, reatomAsync } from '@reatom/framework'
import { useAction, useAtom } from '@reatom/npm-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import styled, { css } from 'styled-components'

import { AsyncStateN, Result } from '@blade/std-lib'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { processBuySwapAction } from '@root/features/trade/swap/atoms/reatom-buy-swap'
import {
  pathfinderDebugOpenAtom,
  PATHFINDER_DEBUG_PRESETS_SOL,
  reatomPathfinderPresetQuotes,
  summarizeQuoteForCard,
} from '@root/features/trade/swap/atoms/reatom-pathfinder-debug'
import { reatomPathfinderQuote } from '@root/features/trade/swap/atoms/reatom-pathfinder-quote'
import { pathfinderServiceAtom } from '@root/features/trade/swap/atoms/reatom-pathfinder-service'
import { pickBestVariant } from '@root/features/trade/swap/services/pathfinder-swap-service'
import {
  tradingPairBaseAtom,
  tradingPairQuoteAtom,
  tradingWalletAddressAtom,
  tradingWalletAtom,
} from '@root/features/trade/trading/atoms'
import { UnknownError } from '@root/shared'
import { Dialog } from '@root/shared/ui-kit'

import type { QuoteResponseBody } from '@blade/pathfinder-api-client'
import type { Ctx } from '@reatom/framework'
import type {
  PathfinderSignAndSendPayload,
  PathfinderSignAndSendResponse,
} from '@root/features/secure-storage/secure-storage-service'
import type { PresetQuoteState } from '@root/features/trade/swap/atoms/reatom-pathfinder-debug'
import type { PathfinderError } from '@root/features/trade/swap/services/pathfinder-swap-service'

const SOL_LAMPORTS_PER_SOL = 1_000_000_000

const solToLamports = (sol: number): bigint =>
  BigInt(Math.trunc(sol * SOL_LAMPORTS_PER_SOL))

const presetsAtom = atom<ReadonlyArray<number>>(
  PATHFINDER_DEBUG_PRESETS_SOL,
  'pathfinderDebugPresetsAtom',
)

export const PathfinderDebugDialog: React.FC = () => {
  const [open, setOpen] = useAtom(pathfinderDebugOpenAtom)

  return (
    <Dialog
      open={open}
      onOpenChange={setOpen}
      describedBy="pathfinder dev probe"
      withClose
      className={dialogClassName}
    >
      <DialogShell>
        <Header>
          <Badge>DEV</Badge>
          <Title>pathfinder probe</Title>
        </Header>
        {open ? <DialogBody /> : undefined}
      </DialogShell>
    </Dialog>
  )
}

const dialogClassName = 'pathfinder-debug-dialog-yellow'

type Direction = 'buy' | 'sell'

const DialogBody: React.FC = () => {
  const [direction, setDirection] = useState<Direction>('buy')
  const [pairBase] = useAtom(tradingPairBaseAtom)
  const [pairQuote] = useAtom(tradingPairQuoteAtom)
  const [taker] = useAtom(tradingWalletAddressAtom)

  const isBuy = direction === 'buy'
  const tokenIn = isBuy ? pairBase?.address : pairQuote?.address
  const tokenOut = isBuy ? pairQuote?.address : pairBase?.address
  const inDecimals = (isBuy ? pairBase?.decimals : pairQuote?.decimals) ?? 9
  const outDecimals = (isBuy ? pairQuote?.decimals : pairBase?.decimals) ?? 9
  const inSymbol = isBuy
    ? (pairBase?.ticker ?? 'SOL')
    : (pairQuote?.ticker ?? 'TOKEN')
  const outSymbol = isBuy
    ? (pairQuote?.ticker ?? 'TOKEN')
    : (pairBase?.ticker ?? 'SOL')

  const [liveAmount, setLiveAmount] = useState('0.05')
  const liveAmountRawAtom = useMemo(
    () =>
      atom<bigint | undefined>((_ctx: Ctx) => {
        const num = Number(liveAmount)
        if (!Number.isFinite(num) || num <= 0) {
          return undefined
        }
        return BigInt(Math.trunc(num * 10 ** inDecimals))
      }, 'pathfinderDebugLiveAmount'),
    [liveAmount, inDecimals],
  )

  const tokenInAtom = useMemo(
    () =>
      atom((ctx: Ctx) => {
        const base =
          ctx.spy?.(tradingPairBaseAtom) ?? ctx.get(tradingPairBaseAtom)
        const quote =
          ctx.spy?.(tradingPairQuoteAtom) ?? ctx.get(tradingPairQuoteAtom)
        return isBuy ? base?.address : quote?.address
      }, `pathfinderDebugTokenIn-${direction}`),
    [direction, isBuy],
  )
  const tokenOutAtom = useMemo(
    () =>
      atom((ctx: Ctx) => {
        const base =
          ctx.spy?.(tradingPairBaseAtom) ?? ctx.get(tradingPairBaseAtom)
        const quote =
          ctx.spy?.(tradingPairQuoteAtom) ?? ctx.get(tradingPairQuoteAtom)
        return isBuy ? quote?.address : base?.address
      }, `pathfinderDebugTokenOut-${direction}`),
    [direction, isBuy],
  )

  const liveQuote = useMemo(
    () =>
      reatomPathfinderQuote({
        serviceAtom: pathfinderServiceAtom,
        amountInRawAtom: liveAmountRawAtom,
        tokenInAtom,
        tokenOutAtom,
        takerAtom: tradingWalletAddressAtom,
        name: `pathfinder-debug-live-${direction}`,
      }),
    [liveAmountRawAtom, tokenInAtom, tokenOutAtom, direction],
  )

  const presetQuotes = useMemo(
    () =>
      reatomPathfinderPresetQuotes({
        presetsAtom,
        serviceAtom: pathfinderServiceAtom,
        tokenInAtom,
        tokenOutAtom,
        takerAtom: tradingWalletAddressAtom,
        name: 'pathfinder-debug-presets',
      }),
    [tokenInAtom, tokenOutAtom],
  )

  const refreshPresets = useAction(presetQuotes.refreshAction)
  const refreshLive = useAction(liveQuote.refreshAction)

  useEffect(() => {
    const onAnySwap = () => {
      refreshLive()
      refreshPresets()
    }
    const unsubBuy = processBuySwapAction.onCall(onAnySwap)
    const unsubPathfinder = processPathfinderSwapAction.onCall(onAnySwap)
    return () => {
      unsubBuy()
      unsubPathfinder()
    }
  }, [refreshLive, refreshPresets])

  return (
    <Body>
      <Tabs>
        <Tab $active={isBuy} onClick={() => setDirection('buy')} type="button">
          BUY
        </Tab>
        <Tab
          $active={!isBuy}
          onClick={() => setDirection('sell')}
          type="button"
        >
          SELL
        </Tab>
      </Tabs>

      <ContextRow>
        <ContextItem>
          <Label>tokenIn ({inSymbol})</Label>
          <Value>{tokenIn ? truncate(tokenIn) : '—'}</Value>
        </ContextItem>
        <ContextItem>
          <Label>tokenOut</Label>
          <Value>{tokenOut ? truncate(tokenOut) : '—'}</Value>
        </ContextItem>
        <ContextItem>
          <Label>taker</Label>
          <Value>{taker ? truncate(taker) : '—'}</Value>
        </ContextItem>
      </ContextRow>

      <Section>
        <SectionHeader>
          <SectionTitle>active quote</SectionTitle>
          <RefreshButton onClick={refreshLive} type="button">
            ↻ refresh
          </RefreshButton>
        </SectionHeader>
        <AmountInput
          type="text"
          inputMode="decimal"
          value={liveAmount}
          onChange={(event) => setLiveAmount(event.target.value)}
          placeholder={`amount in ${inSymbol}`}
        />
        <LiveQuotePanel
          stateAtom={liveQuote.quoteStateAtom}
          variantAtom={liveQuote.bestVariantAtom}
          latencyAtom={liveQuote.lastLatencyMsAtom}
          outDecimals={outDecimals}
          outSymbol={outSymbol}
        />
      </Section>

      {isBuy ? (
        <Section>
          <SectionHeader>
            <SectionTitle>presets</SectionTitle>
            <RefreshButton onClick={refreshPresets} type="button">
              ↻ refresh all
            </RefreshButton>
          </SectionHeader>
          <PresetGrid>
            {PATHFINDER_DEBUG_PRESETS_SOL.map((sol) => (
              <PresetCard
                key={sol}
                sol={sol}
                statesAtom={presetQuotes.statesAtom}
                outDecimals={outDecimals}
                outSymbol={outSymbol}
                onAfterBuy={() => refreshPresets()}
              />
            ))}
          </PresetGrid>
        </Section>
      ) : (
        <SellNote>
          presets disabled in sell direction — token-amount semantics requires a
          balance lookup. use the active quote above with manual input.
        </SellNote>
      )}
    </Body>
  )
}

const LiveQuotePanel: React.FC<{
  stateAtom: ReturnType<typeof reatomPathfinderQuote>['quoteStateAtom']
  variantAtom: ReturnType<typeof reatomPathfinderQuote>['bestVariantAtom']
  latencyAtom: ReturnType<typeof reatomPathfinderQuote>['lastLatencyMsAtom']
  outDecimals: number
  outSymbol: string
}> = ({ stateAtom, variantAtom, latencyAtom, outDecimals, outSymbol }) => {
  const [state] = useAtom(stateAtom)
  const [variant] = useAtom(variantAtom)
  const [latencyMs] = useAtom(latencyAtom)

  if (AsyncStateN.isLoading(state) || AsyncStateN.isNotInitiated(state)) {
    return <Muted>{state.type}…</Muted>
  }
  if (AsyncStateN.isError(state)) {
    return (
      <>
        <ErrorText>
          error: {state.error.message}
          {latencyMs === undefined ? '' : ` · ${latencyMs.toFixed(0)}ms`}
        </ErrorText>
        <ErrorJsonDump error={state.error} />
      </>
    )
  }
  if (!variant) {
    return <Muted>no variants</Muted>
  }
  const summary = summarizeQuoteForCard(state.value)
  if (!summary) {
    return <Muted>no variants</Muted>
  }

  return (
    <DataRow>
      <DataCell>
        <Label>hops</Label>
        <Value>{summary.hops}</Value>
      </DataCell>
      <DataCell>
        <Label>amount out</Label>
        <Value>
          {formatHumanAmount(summary.amountOut, outDecimals)} {outSymbol}
        </Value>
      </DataCell>
      <DataCell>
        <Label>price impact</Label>
        <Value>{(summary.priceImpactBps / 100).toFixed(2)}%</Value>
      </DataCell>
      <DataCell>
        <Label>latency</Label>
        <Value>
          {latencyMs === undefined ? '—' : `${latencyMs.toFixed(0)}ms`}
        </Value>
      </DataCell>
      <DataCell>
        <Label>tx?</Label>
        <Value>{summary.transaction ? 'yes' : 'no'}</Value>
      </DataCell>
    </DataRow>
  )
}

const ErrorJsonDump: React.FC<{ error: PathfinderError }> = ({ error }) => {
  const json = useMemo(() => {
    try {
      const payload =
        typeof (error as { toJSON?: () => unknown }).toJSON === 'function'
          ? (error as { toJSON: () => unknown }).toJSON()
          : { name: error.name, message: error.message, context: error.context }
      return JSON.stringify(payload, undefined, 2)
    } catch (error_) {
      return `[stringify failed: ${(error_ as Error).message}]`
    }
  }, [error])

  const [copied, setCopied] = useState(false)
  const onCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(json)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      setCopied(false)
    }
  }, [json])

  return (
    <DebugJsonWrapper>
      <CopyButton type="button" onClick={onCopy} title="copy json">
        {copied ? '✓' : '⧉'}
      </CopyButton>
      <DebugJsonBlock>{json}</DebugJsonBlock>
    </DebugJsonWrapper>
  )
}

const renderPresetBody = (
  state: PresetQuoteState,
  outDecimals: number,
  outSymbol: string,
): React.ReactNode => {
  if (state.type === 'Loading' || state.type === 'NotInitiated') {
    return <Muted>{state.type}…</Muted>
  }
  if (state.type === 'Error') {
    return (
      <>
        <ErrorText>{state.error.message}</ErrorText>
        <ErrorJsonDump error={state.error} />
      </>
    )
  }
  return (
    <PresetCardData
      state={state.value}
      outDecimals={outDecimals}
      outSymbol={outSymbol}
    />
  )
}

const renderBuyLabel = ({
  submitting,
  isStale,
}: {
  submitting: boolean
  isStale: boolean
}): string => {
  if (submitting) {
    return 'sending…'
  }
  if (isStale) {
    return 'stale, refresh first'
  }
  return 'buy via cached'
}

const PresetCard: React.FC<{
  sol: number
  statesAtom: ReturnType<typeof reatomPathfinderPresetQuotes>['statesAtom']
  outDecimals: number
  outSymbol: string
  onAfterBuy: () => void
}> = ({ sol, statesAtom, outDecimals, outSymbol, onAfterBuy }) => {
  const [statesMap] = useAtom(statesAtom)
  const state: PresetQuoteState =
    statesMap.get(sol) ?? AsyncStateN.notInitiated()
  const [wallet] = useAtom(tradingWalletAtom)
  const submitSwap = useAction(processPathfinderSwapAction)
  const [submitting, setSubmitting] = useState(false)
  const [outcome, setOutcome] = useState<string | undefined>(undefined)

  const cached = state.type === 'Success' ? state.value : undefined
  const now = useTickerNow(1000)
  const ageSec = cached
    ? Math.round((now - cached.fetchedAt) / 1000)
    : undefined
  const isStale = ageSec !== undefined && ageSec > 30
  const variant = cached ? pickBestVariant(cached.quote) : undefined

  const handleBuy = async () => {
    if (!cached || !variant?.transaction || !wallet) {
      return
    }
    setSubmitting(true)
    setOutcome(undefined)
    const result = await submitSwap({
      payload: {
        walletId: wallet.id,
        operation: 'buy',
        txBase64: variant.transaction,
        amountIn: solToLamports(sol).toString(),
        sender: {
          kind: 'helius-sender',
          location: 'default',
        },
      },
    })
    setSubmitting(false)
    if (Result.isSuccess(result)) {
      setOutcome(
        `✓ ${result.success.signature.slice(0, 8)}… (${result.success.totalMs.toFixed(0)}ms)`,
      )
      onAfterBuy()
    } else {
      setOutcome(`✗ ${result.failure.message}`)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardAmount>{sol} SOL</CardAmount>
        {ageSec === undefined ? undefined : (
          <CardAge $stale={isStale}>
            {isStale ? 'stale' : 'fresh'} {ageSec}s
          </CardAge>
        )}
      </CardHeader>
      <CardBody>{renderPresetBody(state, outDecimals, outSymbol)}</CardBody>
      <CardFooter>
        <BuyButton
          onClick={handleBuy}
          disabled={
            !cached || !variant?.transaction || !wallet || submitting || isStale
          }
          type="button"
        >
          {renderBuyLabel({ submitting, isStale })}
        </BuyButton>
        {outcome ? <Outcome>{outcome}</Outcome> : undefined}
      </CardFooter>
    </Card>
  )
}

const PresetCardData: React.FC<{
  state: {
    quote: QuoteResponseBody
    latencyMs: number
  }
  outDecimals: number
  outSymbol: string
}> = ({ state, outDecimals, outSymbol }) => {
  const summary = summarizeQuoteForCard(state.quote)
  if (!summary) {
    return <Muted>no variants</Muted>
  }
  return (
    <CardData>
      <DataCell>
        <Label>hops</Label>
        <Value>{summary.hops}</Value>
      </DataCell>
      <DataCell>
        <Label>out ({outSymbol})</Label>
        <Value>{formatHumanAmount(summary.amountOut, outDecimals)}</Value>
      </DataCell>
      <DataCell>
        <Label>impact</Label>
        <Value>{(summary.priceImpactBps / 100).toFixed(2)}%</Value>
      </DataCell>
      <DataCell>
        <Label>latency</Label>
        <Value>{state.latencyMs.toFixed(0)}ms</Value>
      </DataCell>
    </CardData>
  )
}

const formatHumanAmount = (rawAmount: number, decimals: number): string => {
  if (!Number.isFinite(rawAmount)) {
    return '—'
  }
  const human = rawAmount / 10 ** decimals
  if (human === 0) {
    return '0'
  }
  if (Math.abs(human) >= 1e6) {
    return `${(human / 1e6).toFixed(2)}M`
  }
  if (Math.abs(human) >= 1e3) {
    return `${(human / 1e3).toFixed(2)}K`
  }
  if (Math.abs(human) >= 1) {
    return human.toFixed(4)
  }
  return human.toPrecision(4)
}

const useTickerNow = (intervalMs: number): number => {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs)
    return () => window.clearInterval(id)
  }, [intervalMs])
  return now
}

const truncate = (address: string) =>
  `${address.slice(0, 4)}…${address.slice(-4)}`

const yellowBorderCss = css`
  border: 2px dashed var(--yellow-9, #eab308) !important;
  box-shadow: 0 0 0 1px var(--yellow-9, #eab308) !important;
`

const DialogShell = styled.div`
  width: 720px;
  max-width: calc(100vw - 40px);
  display: grid;
  grid-template-rows: auto 1fr;

  // hack: target the parent dialog Body via :where so we add yellow border
  .${dialogClassName} & {
    ${yellowBorderCss}
  }
`

const Header = styled.div`
  display: grid;
  grid-auto-flow: column;
  justify-content: start;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--background-3);
  background: var(--background-2);
`

const Badge = styled.span`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  background: var(--yellow-9, #eab308);
  color: var(--appearance-1);
  padding: 2px 6px;
  border-radius: 4px;
`

const Title = styled.span`
  color: var(--text-12);
  font-size: 14px;
  font-weight: 600;
`

const Body = styled.div`
  padding: 16px;
  display: grid;
  gap: 16px;
`

const ContextRow = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  gap: 8px;
  padding: 8px 12px;
  background: var(--background-2);
  border-radius: 8px;
`

const ContextItem = styled.div`
  display: grid;
  gap: 2px;
`

const Section = styled.section`
  display: grid;
  gap: 8px;
`

const SectionHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
`

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--text-9);
`

const RefreshButton = styled.button`
  background: transparent;
  border: 1px solid var(--background-4);
  color: var(--text-11);
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  cursor: pointer;

  &:hover {
    background: var(--background-3);
    color: var(--text-12);
  }
`

const AmountInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid var(--background-4);
  background: var(--background-2);
  color: var(--text-12);
  font-family: inherit;
  font-size: 13px;

  &:focus {
    outline: none;
    border-color: var(--yellow-9, #eab308);
  }
`

const DataRow = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  gap: 8px;
  padding: 8px 12px;
  background: var(--background-2);
  border-radius: 6px;
`

const DataCell = styled.div`
  display: grid;
  gap: 2px;
`

const Label = styled.span`
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--text-9);
`

const Value = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: var(--text-12);
  font-variant-numeric: tabular-nums;
`

const Muted = styled.div`
  color: var(--text-9);
  font-size: 12px;
  padding: 8px 0;
`

const ErrorText = styled.div`
  color: var(--down-9, #ef4444);
  font-size: 11px;
  word-break: break-word;
`

const DebugJsonWrapper = styled.div`
  position: relative;
  margin-top: 6px;
`

const DebugJsonBlock = styled.pre`
  margin: 0;
  padding: 8px;
  padding-right: 32px;
  border: 1px dashed var(--yellow-9, #eab308);
  border-radius: 6px;
  background: var(--appearanceA-5);
  color: var(--yellow-9, #eab308);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 10px;
  line-height: 1.4;
  max-height: 220px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-all;
`

const CopyButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 1;
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  background: var(--appearanceA-6);
  border: 1px solid var(--yellow-9, #eab308);
  border-radius: 4px;
  color: var(--yellow-9, #eab308);
  font-size: 12px;
  cursor: pointer;

  &:hover {
    background: var(--yellow-9);
    color: var(--appearance-1);
  }
`

const Tabs = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
  padding: 2px;
  background: var(--background-2);
  border-radius: 8px;
`

const Tab = styled.button<{ $active: boolean }>`
  background: ${(p) => (p.$active ? 'var(--yellow-9)' : 'transparent')};
  color: ${(p) => (p.$active ? 'black' : 'var(--background-11)')};
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.05em;
  cursor: pointer;

  &:hover {
    background: ${(p) =>
      p.$active ? 'var(--yellow-9)' : 'var(--background-3)'};
  }
`

const SellNote = styled.div`
  font-size: 11px;
  color: var(--text-9);
  font-style: italic;
  padding: 12px;
  border: 1px dashed var(--background-4);
  border-radius: 6px;
  background: var(--background-2);
`

const PresetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 8px;
`

const Card = styled.div`
  display: grid;
  grid-template-rows: auto 1fr auto;
  gap: 8px;
  padding: 10px;
  border: 1px dashed var(--background-4);
  border-radius: 8px;
  background: var(--background-2);
`

const CardHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
`

const CardAmount = styled.span`
  font-weight: 700;
  font-size: 13px;
  color: var(--text-12);
`

const CardAge = styled.span<{ $stale: boolean }>`
  font-size: 10px;
  color: ${(p) =>
    p.$stale ? 'var(--down-9, #ef4444)' : 'var(--up-9, #22c55e)'};
`

const CardBody = styled.div`
  display: grid;
`

const CardData = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
`

const CardFooter = styled.div`
  display: grid;
  gap: 4px;
`

const BuyButton = styled.button`
  background: var(--yellow-9, #eab308);
  color: var(--appearance-1);
  border: none;
  border-radius: 6px;
  padding: 6px 10px;
  font-weight: 600;
  font-size: 11px;
  cursor: pointer;

  &:disabled {
    background: var(--background-4);
    color: var(--text-9);
    cursor: not-allowed;
  }
`

const Outcome = styled.span`
  font-size: 10px;
  color: var(--text-11);
  font-family: monospace;
  word-break: break-word;
`

interface ProcessPathfinderSwapArgs {
  payload: PathfinderSignAndSendPayload
  onSuccess?: (response: PathfinderSignAndSendResponse) => void
}

const processPathfinderSwapAction = reatomAsync(
  async (
    ctx,
    args: ProcessPathfinderSwapArgs,
  ): Promise<Result.Result<PathfinderSignAndSendResponse, UnknownError>> => {
    const { secureStorageService } = ctx.get(dependenciesAtom)
    const result = await ctx.schedule(() =>
      secureStorageService.singAndSendPathfinderSwapTransaction(args.payload),
    )

    if (Result.isFailure(result)) {
      return Result.fail(UnknownError.fromUnknown(result.failure))
    }

    args.onSuccess?.(result.success)
    return Result.succeed(result.success)
  },
  'processPathfinderSwapAction',
)
